/**
 * 新規登録とログインの切替
 * 新規登録画面・ログイン画面の本文最上部に置き、どちらの画面にいるかと、もう一方への入口を左右2分割で示します
 */

import React from 'react';
import { Pressable, Text, View } from '../../../shared/components/WebPrimitives';

/** 切替の対象となる画面 */
export type VisitorAuthMode = 'register' | 'login';

/** 切替の左右の並びと表示文言（左が新規登録、右がログイン） */
const AUTH_MODE_OPTIONS: ReadonlyArray<{ mode: VisitorAuthMode; caption: string; label: string }> = [
  { mode: 'register', caption: 'はじめての方', label: '新規登録' },
  { mode: 'login', caption: '登録済みの方', label: 'ログイン' },
];

/** 左右どちらにも共通する枠の見た目 */
const OPTION_BASE_CLASS_NAME = 'min-h-[48px] min-w-0 flex-1 items-center justify-center rounded-none px-2 py-2';

/**
 * 切替の片側の文言（補足と画面名）を表示する
 * @param {Object} props - プロパティ
 * @param {string} props.caption - 小さめの補足
 * @param {string} props.label - 画面名
 * @param {boolean} props.isCurrent - 表示中の画面の側か
 * @returns {React.JSX.Element} 切替の片側の文言
 */
const AuthModeOptionText = ({
  caption,
  label,
  isCurrent,
}: {
  caption: string;
  label: string;
  isCurrent: boolean;
}): React.JSX.Element => (
  <>
    <Text className={`text-center text-xs font-semibold ${isCurrent ? 'text-white' : 'text-app-text-secondary'}`}>
      {caption}
    </Text>
    <Text className={`text-center text-base font-extrabold ${isCurrent ? 'text-white' : 'text-app-quest-night underline'}`}>
      {label}
    </Text>
  </>
);

/**
 * 新規登録とログインの切替を表示する
 * 表示中の側は塗りつぶして`aria-current="page"`を付け、押しても移動しない。もう一方の側だけがリンクになる
 * @param {Object} props - プロパティ
 * @param {VisitorAuthMode} props.current - 表示中の画面
 * @param {() => void} props.onSwitch - もう一方の画面へ移る操作
 * @returns {React.JSX.Element} 新規登録とログインの切替
 */
const VisitorAuthModeSwitch = ({
  current,
  onSwitch,
}: {
  current: VisitorAuthMode;
  onSwitch: () => void;
}): React.JSX.Element => (
  <View
    accessibilityLabel="新規登録とログインの切替"
    className="flex-row rounded-none border-2 border-app-quest-night"
    role="navigation"
  >
    {AUTH_MODE_OPTIONS.map(({ mode, caption, label }) => (
      mode === current ? (
        <View
          aria-current="page"
          className={`${OPTION_BASE_CLASS_NAME} bg-app-quest-night`}
          key={mode}
          testID={`auth-mode-switch-${mode}`}
        >
          <AuthModeOptionText caption={caption} isCurrent label={label} />
        </View>
      ) : (
        <Pressable
          accessibilityRole="link"
          className={`${OPTION_BASE_CLASS_NAME} bg-[#EAF3FF]`}
          key={mode}
          onPress={onSwitch}
          testID={`auth-mode-switch-${mode}`}
        >
          <AuthModeOptionText caption={caption} isCurrent={false} label={label} />
        </Pressable>
      )
    ))}
  </View>
);

export default VisitorAuthModeSwitch;
